import { z } from 'zod';
import { INTERNAL_ONLY } from '../../domain/entities/common.ts';
import {
  ALLOWED_CONTENT_TYPES,
  FileSchema,
  folderKindFor,
  LARGE_FILE_BYTES,
  linkFields,
  MAX_FILE_BYTES,
  type File,
} from '../../domain/entities/file.ts';
import type { RecordRef } from '../../domain/entities/notification.ts';
import { newId } from '../../domain/id.ts';
import type { ClockPort } from '../../ports/clock-port.ts';
import type { DataPort } from '../../ports/data-port.ts';
import { NotFoundError } from '../../ports/repository.ts';
import type { StoragePath, StoragePort } from '../../ports/storage-port.ts';
import type { Visibility } from '../../domain/entities/common.ts';

/**
 * Uploading a file.
 *
 * Two stores, one act, and they cannot be made atomic: the bytes go to
 * storage and the record to Firestore. So the order is chosen for which
 * failure is survivable.
 *
 * Bytes first, then the document. An object with no document is invisible
 * and costs storage — a tidy-up job's problem. A document with no object is
 * a file that appears in every list, cannot be opened, and has to be
 * explained to whoever uploaded it. The second is much worse, so the
 * document is written last.
 */
export class FileUploadError extends Error {
  readonly reason: 'tooLarge' | 'unsupportedType' | 'noName';

  constructor(reason: 'tooLarge' | 'unsupportedType' | 'noName', message: string) {
    super(message);
    this.name = 'FileUploadError';
    this.reason = reason;
  }
}

export const UploadFileInputSchema = z.object({
  name: z.string().trim().min(1, 'A file needs a name').max(260),
  contentType: z.string().min(1),
  size: z.number().int().nonnegative(),
  /** Absent for a company-level file: a template, a form, a logo. */
  jobId: z.string().min(1).optional(),
  folderId: z.string().min(1).optional(),
  visibility: z.custom<Visibility>().optional(),
  tagIds: z.array(z.string().min(1)).optional(),
  linkedRecords: z.array(z.custom<RecordRef>()).optional(),
});
export type UploadFileInput = z.input<typeof UploadFileInputSchema>;

export interface UploadFileContext {
  readonly data: DataPort;
  readonly storage: StoragePort;
  readonly clock: ClockPort;
  readonly companyId: string;
  readonly uid: string;
}

/**
 * Whether a file is big enough to be worth mentioning before it is sent.
 *
 * Checked by the caller, not here: the point is to warn *before* a long
 * upload, and a function that has already been handed the bytes is too late
 * to be useful.
 */
export function isLargeFile(size: number): boolean {
  return size >= LARGE_FILE_BYTES;
}

/** Refuse before spending an upload on something that will be rejected. */
export function checkUploadable(input: { contentType: string; size: number }): void {
  if (input.size > MAX_FILE_BYTES) {
    throw new FileUploadError(
      'tooLarge',
      `That file is larger than ${Math.round(MAX_FILE_BYTES / 1024 / 1024)} MB`,
    );
  }
  if (!ALLOWED_CONTENT_TYPES.includes(input.contentType)) {
    // Named rather than listed: a person who tried to upload an .exe does
    // not want twenty content types read back at them.
    throw new FileUploadError('unsupportedType', 'That kind of file cannot be uploaded here');
  }
}

/** Where the bytes go, which is also what the storage rules match on. */
export function storagePathFor(
  companyId: string,
  fileId: string,
  contentType: string,
  jobId?: string,
): StoragePath {
  return { companyId, jobId, folder: folderKindFor(contentType), fileId };
}

export interface UploadedFile {
  readonly file: File;
  /**
   * Present only when the adapter could produce one without reading the
   * bucket — which the Firebase adapter no longer may, because storage
   * rules refuse every client read (P3-04c). Ask `downloadUrlFor` for a
   * link instead; it mints one through a function that checks who is
   * asking. Kept for the in-memory adapter, where there is no such rule and
   * an immediate preview costs nothing.
   */
  readonly url?: string;
}

/**
 * Send the bytes, then record the file.
 *
 * The record lands as `processing`: the checks that decide whether it may be
 * served have not run yet, and a client is not allowed to claim they have.
 * A function moves it on.
 */
export async function uploadFile(
  context: UploadFileContext,
  input: UploadFileInput,
  data: Blob | Uint8Array,
  onProgress?: (fraction: number) => void,
): Promise<UploadedFile> {
  const parsed = UploadFileInputSchema.safeParse(input);
  if (!parsed.success) {
    throw new FileUploadError('noName', parsed.error.issues[0]!.message);
  }
  // Before the upload, not after: refusing afterwards still spends the
  // bandwidth and the storage write.
  checkUploadable(parsed.data);

  const id = newId();
  const path = storagePathFor(context.companyId, id, parsed.data.contentType, parsed.data.jobId);

  const stored = await context.storage.upload(
    path,
    data,
    {
      contentType: parsed.data.contentType,
      fileName: parsed.data.name,
      size: parsed.data.size,
    },
    onProgress,
  );

  const now = context.clock.now().toISOString();
  const file: File = {
    id,
    companyId: context.companyId,
    name: parsed.data.name,
    storagePath: `${path.companyId}/${path.jobId ?? '-'}/${path.folder}/${path.fileId}`,
    // The size storage reports, not the size the caller claimed. A client
    // that could state its own size could lie to a quota.
    size: stored.size,
    contentType: parsed.data.contentType,
    jobId: parsed.data.jobId,
    folderId: parsed.data.folderId,
    uploadedBy: context.uid,
    status: 'processing',
    visibility: parsed.data.visibility ?? INTERNAL_ONLY,
    tagIds: parsed.data.tagIds ?? [],
    ...linkFields(parsed.data.linkedRecords ?? []),
    createdAt: now,
    createdBy: context.uid,
    updatedAt: now,
    updatedBy: context.uid,
  };

  const checked = FileSchema.safeParse(file);
  if (!checked.success) throw new FileUploadError('noName', checked.error.issues[0]!.message);

  await files(context).create(file);
  return { file, ...(stored.url === undefined ? {} : { url: stored.url }) };
}

function files(context: { data: DataPort; companyId: string }) {
  return context.data.collection<File>({ companyId: context.companyId }, 'files');
}

/**
 * Rename, re-file, re-tag or re-share a file.
 *
 * Deliberately cannot touch the bytes, the size, the uploader or the
 * status — the same narrowing the security rules enforce, stated here so
 * the two agree rather than only happening to.
 */
export const EditFileInputSchema = z.object({
  name: z.string().trim().min(1).max(260).optional(),
  folderId: z.string().min(1).nullish(),
  tagIds: z.array(z.string().min(1)).optional(),
  visibility: z.custom<Visibility>().optional(),
  linkedRecords: z.array(z.custom<RecordRef>()).optional(),
});
export type EditFileInput = z.input<typeof EditFileInputSchema>;

export async function editFile(
  context: UploadFileContext,
  id: string,
  input: EditFileInput,
): Promise<File> {
  const parsed = EditFileInputSchema.safeParse(input);
  if (!parsed.success) throw new FileUploadError('noName', parsed.error.issues[0]!.message);

  const repo = files(context);
  const existing = await repo.get(id);
  if (!existing) throw new NotFoundError('files', id);

  const now = context.clock.now().toISOString();
  const next: File = {
    ...existing,
    name: parsed.data.name ?? existing.name,
    folderId:
      parsed.data.folderId === undefined ? existing.folderId : (parsed.data.folderId ?? undefined),
    tagIds: parsed.data.tagIds ?? existing.tagIds,
    visibility: parsed.data.visibility ?? existing.visibility,
    // Both derived together, so the query field cannot fall behind the one
    // the UI reads.
    ...linkFields(parsed.data.linkedRecords ?? existing.linkedRecords),
    updatedAt: now,
    updatedBy: context.uid,
  };

  const { id: _id, ...patch } = next;
  await repo.update(id, patch);
  return next;
}

/**
 * Attach a file to a record, or detach it.
 *
 * Both sides hold references. Deleting the record never deletes the file —
 * the photographs taken on a day outlive the daily log that mentioned them,
 * and `docs/features/11-files-messaging.md` §1 is explicit about it.
 */
export async function linkFileTo(
  context: UploadFileContext,
  id: string,
  record: RecordRef,
): Promise<File> {
  const existing = await files(context).get(id);
  if (!existing) throw new NotFoundError('files', id);
  const already = existing.linkedRecords.some(
    (link) => link.kind === record.kind && link.id === record.id,
  );
  // Attaching twice is not an error; it is a double click.
  if (already) return existing;
  return editFile(context, id, { linkedRecords: [...existing.linkedRecords, record] });
}

export async function unlinkFileFrom(
  context: UploadFileContext,
  id: string,
  record: RecordRef,
): Promise<File> {
  const existing = await files(context).get(id);
  if (!existing) throw new NotFoundError('files', id);
  return editFile(context, id, {
    linkedRecords: existing.linkedRecords.filter(
      (link) => !(link.kind === record.kind && link.id === record.id),
    ),
  });
}
